import React, { useState } from 'react';

import Image from 'next/image';

import Typography from '@components/Typography';
import { Row as AntRow } from '@components/Grid';
import { RoomFeatureViewModel } from '@api/index';
import { Col, Row, FlexibleDiv } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { HotelIcon } from '@assets/icons';
import ImageWithFallback from '@components/utils/ImageWithFallback';

import { DetailedRoomProps } from '../RoomOptions';
import {
  Description,
  DistantRow,
  ImagesWrapper,
  LeftField,
  RightField,
  RoomCard,
  RoomFeatures,
  SmallImage,
  SmallImagesWrapper,
  Title,
  ShowMore,
  PopoverWrapper,
  AvailableDatesWrapper,
} from './DetailedRoomStyle';
import { ModalView } from '../details-galleries-modal';
import AvailableRoomDates from '../room-selection/components/AvailableRoomDates';
import { cloudineryImage } from '../../utils/features';

export const FeaturesPopover = ({
  features,
}: {
  features: RoomFeatureViewModel[];
}) => (
  <PopoverWrapper>
    {features?.map((feature) => (
      <RoomFeatures key={feature.name}>{feature.name}</RoomFeatures>
    ))}
  </PopoverWrapper>
);

const DetailedRoomDesktop = ({
  type,
  roomTypeName,
  images,
  features,
  showRoom,
  availableDates,
  isFetchingAvailableDates,
  isLocalHotel,
}: DetailedRoomProps) => (
  <RoomCard>
    <DistantRow>
      <Row>
        <FlexibleDiv top={10} right={15}>
          <CustomIcon paletteColor='coral' fontSize={26} icon={HotelIcon} />
        </FlexibleDiv>
        <Col>
          <Title title={type}>
            <Typography variant='Title4' color='D400' weight='Bold'>
              {type}
            </Typography>
          </Title>
          <Description>
            <Typography variant='Title5' color='D400'>
              {roomTypeName}
            </Typography>
          </Description>
        </Col>
      </Row>
    </DistantRow>
    <ImagesWrapper>
      <LeftField>
        <ImageWithFallback
          Component={Image}
          width={405}
          height={240}
          src={cloudineryImage(
            images[0]?.url ?? '/static/images/setur-static.jpg',
            '405',
            '240'
          )}
          alt={`${roomTypeName} Oda 1`}
          onClick={() => showRoom(ModalView.GALLERIES)}
          style={{ objectFit: 'cover', borderRadius: 8 }}
        />
      </LeftField>
      <RightField isImageExist={images?.slice(1, 4).length > 0 ? true : false}>
        <SmallImagesWrapper>
          {images?.slice(1, 4).map((image, index) => (
            <Thumbnail
              key={index}
              {...image}
              alt={`${roomTypeName} Oda ${index + 2}`}
            />
          ))}
        </SmallImagesWrapper>
        {features?.length ? (
          <>
            <Typography variant='caption1' color='D400' weight='SemiBold'>
              Oda özellikleri
            </Typography>

            <AntRow>
              {features?.slice(0, 3).map((feature) => (
                <RoomFeatures key={feature.name}>
                  <Typography variant='caption2' color='D400'>
                    {feature.name}
                  </Typography>
                </RoomFeatures>
              ))}
              {features.length > 3 ? (
                <ShowMore onClick={() => showRoom(ModalView.DETAILS)}>
                  +{features.length - 3}
                </ShowMore>
              ) : null}
            </AntRow>
          </>
        ) : null}
        {isLocalHotel ? (
          <AvailableDatesWrapper>
            <AvailableRoomDates
              availableDates={availableDates}
              isFetchingAvailableDates={isFetchingAvailableDates}
            />
          </AvailableDatesWrapper>
        ) : null}
      </RightField>
    </ImagesWrapper>
  </RoomCard>
);

export default DetailedRoomDesktop;

const Thumbnail = ({
  url = '/static/images/setur-static.jpg',
  alt,
}: {
  url?: string;
  alt: string;
}) => {
  const [error, setError] = useState(false);
  const style = error ? { display: 'none' } : {};
  return (
    <SmallImage style={style}>
      <Image
        width={103}
        height={62}
        src={cloudineryImage(
          url ?? '/static/images/setur-static.jpg',
          '103',
          '62'
        )}
        alt={alt || 'Setur Görseli Hazırlanmakta'}
        onError={() => setError(true)}
      />
    </SmallImage>
  );
};
